import React, { useEffect, useMemo, useState } from 'react';
import CapabilityGrid from './CapabilityGrid';
import CreateRole from './CreateRole';
import { pillActive, pillInactive } from '../styles';
import { useOrderedSubtabs } from '../subtabs';
import { ROLES_SUBTAB_CAPS } from './types';

type SubtabKey = 'create-catalyx' | 'create-client' | 'catalyx' | 'client' | 'tier';

const SUBTABS: { key: SubtabKey; label: string; cap: string }[] = [
  { key: 'create-catalyx', label: 'Create Catalyx Role', cap: ROLES_SUBTAB_CAPS.createCatalyx },
  { key: 'create-client', label: 'Create Client Role', cap: ROLES_SUBTAB_CAPS.createClient },
  { key: 'catalyx', label: 'Catalyx Capabilities', cap: ROLES_SUBTAB_CAPS.catalyx },
  { key: 'client', label: 'Client Capabilities', cap: ROLES_SUBTAB_CAPS.client },
  { key: 'tier', label: 'Tier Capabilities', cap: ROLES_SUBTAB_CAPS.tier },
];

const AdminRolesCapabilitiesTab: React.FC = () => {
  const declared = useMemo(() => SUBTABS, []);
  const visibleSubtabs = useOrderedSubtabs('roles_capabilities', declared);
  const [active, setActive] = useState<SubtabKey | null>(null);

  useEffect(() => {
    setActive((current) => {
      if (current && visibleSubtabs.some((t) => t.key === current)) return current;
      return (visibleSubtabs[0]?.key as SubtabKey | undefined) ?? null;
    });
  }, [visibleSubtabs]);

  if (visibleSubtabs.length === 0) {
    return (
      <div className="text-sm text-[#8A8A8A]">
        You do not have access to any role or capability tools in this panel.
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5">
        {visibleSubtabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActive(tab.key)}
            className={`h-8 px-3 rounded-full text-[13px] font-medium transition-colors ${
              active === tab.key ? pillActive : pillInactive
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {active === 'create-catalyx' && <CreateRole kind="catalyx" />}
      {active === 'create-client' && <CreateRole kind="client" />}
      {active === 'catalyx' && <CapabilityGrid scope="catalyx" />}
      {active === 'client' && <CapabilityGrid scope="client" />}
      {active === 'tier' && <CapabilityGrid scope="tier" />}
    </div>
  );
};

export default AdminRolesCapabilitiesTab;
